import { lumi } from '@/lib/lumi'
import { useState, useEffect } from 'react'

export interface Task {
  _id?: string
  title: string
  description: string
  category: string
  budget: number
  deadline: string
  status: 'open' | 'created' | 'claimed' | 'in_progress' | 'submitted' | 'verified' | 'completed' | 'disputed'
  requesterId: string
  agentId?: string
  taskType?: 'one_time' | 'recurring' | 'subscription' | 'auction'
  requirements?: string
  expectedOutput?: string
  inputFormat?: string
  privacyLevel?: 'public' | 'private'
  escrowTxHash?: string
  submittedOutput?: string
  completionTxHash?: string
  creator: string
  createdAt: string
  updatedAt: string
}

export function useTasks() {
  const [tasks, setTasks] = useState<Task[]>([])
  const [loading, setLoading] = useState(false)

  const fetchTasks = async (filter?: any) => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.tasks.list({
        filter: filter || {},
        sort: { createdAt: -1 }
      })
      // Filter out deleted tasks
      const activeTasks = list.filter(task => task.status !== 'deleted')
      setTasks(activeTasks)
      console.log('tasks fetched (active only):', activeTasks)
    } catch (error) {
      console.error('Fetch tasks failed:', error)
    } finally {
      setLoading(false)
    }
  }

  const createTask = async (taskData: Partial<Task>) => {
    try {
      const task = await lumi.entities.tasks.create(taskData)
      console.log('task created:', task)
      await fetchTasks()
      return task
    } catch (error) {
      console.error('Create task failed:', error)
      throw error
    }
  }

  const updateTask = async (taskId: string, updates: Partial<Task>) => {
    try {
      await lumi.entities.tasks.update(taskId, updates)
      console.log('task updated:', taskId)
      await fetchTasks()
    } catch (error) {
      console.error('Update task failed:', error)
      throw error
    }
  }

  const claimTask = async (taskId: string, agentId: string) => {
    try {
      await lumi.entities.tasks.update(taskId, {
        status: 'claimed',
        agentId: agentId
      })
      console.log('task claimed:', taskId)
      await fetchTasks()
    } catch (error) {
      console.error('Claim task failed:', error)
      throw error
    }
  }

  const deleteTask = async (taskId: string) => {
    try {
      await lumi.entities.tasks.update(taskId, {
        status: 'deleted',
        updatedAt: new Date().toISOString()
      })
      console.log('task deleted:', taskId)
      await fetchTasks()
    } catch (error) {
      console.error('Delete task failed:', error)
      throw error
    }
  }

  useEffect(() => {
    fetchTasks()
  }, [])

  return { tasks, loading, fetchTasks, createTask, updateTask, claimTask, deleteTask }
}
